import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Location } from '@angular/common';

import { AuthenticationService } from '~/core/services/authentication.service';
import { RoutedDialogService } from '~/core/services/routed-dialog.service';
import { CompanyService } from '~/core/services/company.service';
import { AccountabilityMethod, Company } from '~/core/models/company';
import { UserProfile } from '~/core/constants/user-profile';
import { SnackbarService } from '~/core/services/snackbar.service';
import { NavigationService } from '~/core/services/navigation.service';
import { StorageService } from '~/core/services/storage.service';
import { CookiePermissionService } from '~/core/services/cookie-permission.service';
import { isDesktop } from '~/core/helpers/device-utils';
import { UserStore } from '~/core/store/user.store';
import { CompanyStore } from '~/core/store/company.store';
export class AuthenticationField {
  companyCredit = true;
  company: Company;
  isRegister;
  idAffiliate;
  showAuth = false;
  showRecaptcha = false;
  isLoggedIn = false;
  isGuestPlayer = false;
  isEnableRegistration = false;
  isEnableGuestPlayers = false;
  shouldUseRecaptchaV2 = false;

  public loadingExit = false;

  protected subscriptions: Array<Subscription> = [];
  protected readonly userStore: UserStore = inject(UserStore);
  protected readonly companyStore: CompanyStore = inject(CompanyStore);
  protected readonly routedDialogService: RoutedDialogService = inject(RoutedDialogService);

  constructor(
    public dialog: MatDialog,
    public authentication: AuthenticationService,
    public companyService: CompanyService,
    public router: Router,
    public route: ActivatedRoute,
    public snackbarService: SnackbarService,
    public translate: TranslateService,
    protected navigation: NavigationService,
    protected location: Location,
    protected cookiesService: CookiePermissionService,
    protected storageService: StorageService,
  ) {}

  destroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  init() {
    this.updatePropertiesByCompany();
    this.subscriptions.push(
      this.companyStore.companyObservable().subscribe(() => {
        this.updatePropertiesByCompany();
      }),
    );
  }

  handleRegisterRoute() {
    const params = new URLSearchParams(window.location.search);
    const queryParams: Record<string, string> = Object.fromEntries(params.entries());

    if (queryParams) {
      this.idAffiliate = queryParams.id;

      this.authentication.handleRegisterRoute(queryParams);
    }
    if (!this.isRegister && this.dialog.openDialogs.length === 0) {
      this.isRegister = true;
      this.openRegisterDialog();
    }
  }

  updatePropertiesByCompany() {
    this.company = this.companyStore.companySignal();
    if (this.router.url.includes('register')) {
      this.handleRegisterRoute();
    }
    this.isEnableRegistration = this.enableRegistration();
    this.isEnableGuestPlayers = this.companyService.isEnabledGuestPlayers;
    this.companyCredit = this.company.accountability_method !== AccountabilityMethod.COUNTING;
    if (this.authentication.isCurrentUserClient || this.hasOnlineAgentActive()) {
      this.companyCredit = true;
    }
  }

  async hasOnlineAgentActive() {
    const currentUser = this.userStore.userSignal();
    const company = this.companyStore.companySignal();
    const userType = currentUser?.type;
    const companyAgent = company?.online_agent;
    const userAgentEnabled = currentUser?.zone?.online_agent?.enable;
    return (
      [UserProfile.MASTER, UserProfile.ADMIN, UserProfile.USERS_ADMIN, UserProfile.EVENTS_ADMIN].includes(userType) ||
      ([UserProfile.MANAGER, UserProfile.SUPERVISOR].indexOf(userType) !== -1 && companyAgent && userAgentEnabled)
    );
  }

  get isDesktop() {
    return isDesktop();
  }

  openRegisterDialog(isClick?: boolean): void {
    if (!this.storageService.cookiesEssentialsAllowed) {
      this.subscriptions.push(
        this.cookiesService.permissions$.essentials.subscribe((isGranted: boolean) => {
          if (isGranted) {
            this.afterHandleRegister(isClick);
          }
        }),
      );
    } else {
      this.afterHandleRegister(isClick);
    }
  }

  private afterHandleRegister(isClick: boolean) {
    // TODO(MIGRATION-AUTH-FIELD-GLOBAL-MODE): static getter - cannot use inject()
    if (CompanyService.isGlobalMode) {
      this.router.navigate(['register-user']);
    } else {
      this.authentication.openRegisterScreen(this.location, isClick);
    }
  }

  openLoginDialogDeposit(): void {
    this.openRequestDeposit();
  }

  openRequestDeposit() {
    const companyConfig = this.companyStore.configSignal();
    if (companyConfig?.client?.features?.deposit?.create) {
      void this.routedDialogService.openDeposit();
    }
  }

  enableRegistration() {
    const company = this.companyStore.companySignal();
    return company && company.client_registration;
  }

  logout() {
    this.loadingExit = true;
    this.authentication.logout();
    this.isLoggedIn = false;
    this.companyService.updateCurrentCompany();
    const currentUrl = this.router.url;

    if (['/my-bets', '/affiliate', '/promotion-bonus'].includes(currentUrl)) {
      this.router.navigate(['']);
    }
    this.loadingExit = false;
  }

  getUserBalance(): number {
    const user = this.userStore?.userSignal?.() || AuthenticationService.currentUser;
    return user && user.credits;
  }

  getUserBalanceWithBonus(): number {
    const user = this.userStore?.userSignal?.() || AuthenticationService.currentUser;
    let totalBalance = user?.credits || 0;
    totalBalance += user?.bonus_credits || 0;
    if (user?.casinoBonus && Array.isArray(user?.casinoBonus)) {
      totalBalance += user?.casinoBonus.reduce((sum, bonus) => sum + (bonus.rollover?.remaining_value || 0), 0);
    }
    if (user?.sportsbookBonus && Array.isArray(user?.sportsbookBonus)) {
      totalBalance += user?.sportsbookBonus.reduce((sum, bonus) => sum + (bonus.rollover?.remaining_value || 0), 0);
    }
    return totalBalance;
  }
}
